import { setUser, startSpan } from '@sentry/react';
import { signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { Tokens } from '@/amplify/types';
import { pushService } from '@/services/push-service.ts';
import { traceError } from '@/utils/trace-error';

type SessionData = {
  tokens?: Tokens;
};

let sessionDataPromise: Promise<SessionData> | undefined;

let forceRefresh = false;
let user: {
  id: string;
  username?: string;
  email: string;
} | null;

export const invalidateSession = () => {
  sessionDataPromise = undefined;

  // force refresh needed to update preferred username after user selects one during signup
  forceRefresh = true;
};

const fetchSession = async () => {
  const tokens = (await startSpan({ name: 'ui.get_tokens' }, () =>
    cognitoUserPoolsTokenProvider.getTokens({
      forceRefresh,
    }),
  )) as Tokens | undefined;

  forceRefresh = false;

  if (tokens?.idToken?.payload.sub) {
    if (!user || forceRefresh) {
      user = {
        id: tokens.idToken.payload.sub,
        username: tokens.idToken.payload.preferred_username,
        email: tokens.idToken.payload.email,
      };

      window.mixpanel?.identify(user.id);
      window.mixpanel?.people.set({
        $name: user.username,
        $email: user.email,
      });

      setUser(user);
    }
  }

  return { tokens };
};

export const getAuthorizedData = () =>
  (sessionDataPromise ??= fetchSession())
    .catch(async (err) => {
      if (err.name === 'NetworkError') {
        throw err;
      }

      traceError(new Error('Failed to fetch session data', { cause: err }));

      try {
        const subscription = await pushService.getSubscription();
        if (subscription) await subscription.unsubscribe();
      } catch (err) {
        traceError(
          new Error(
            'Failed to unsubscribe from push notifications on token expire',
            {
              cause: err,
            },
          ),
        );
      }

      // Leave Privy alone for same-user re-login. A different Synoptic user is
      // handled by PrivySynopticUserGuard on the next terminal visit.
      await signOut();

      return {
        tokens: undefined,
      } satisfies SessionData;
    })
    .finally(() => {
      sessionDataPromise = undefined;
    });
